// ─── editableTemplates.ts ────────────────────────────────────────────────────
// Templates: 2 = Service Contract, 3 = Modern Resume, 4 = NDA Agreement

const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

// ── Shared helpers ─────────────────────────────────────────────────────────

export const esc = (s = '') =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Inline editable span or static text */
export const fe = (id: string, ph: string, val: string, ed: boolean): string =>
  ed
    ? `<span class="e" contenteditable="true" id="f_${id}" data-ph="${ph}">${val}</span>`
    : esc(val) || ph;

/** Block-level editable div or static div */
export const fbe = (id: string, ph: string, val: string, ed: boolean): string =>
  ed
    ? `<div class="e" contenteditable="true" id="f_${id}" data-ph="${ph}" style="min-height:18px">${val}</div>`
    : `<div>${esc(val) || ph}</div>`;

/** Extract all f_* fields from an iframe document */
export const extractAllFields = (doc: Document): Record<string, string> => {
  const data: Record<string, string> = {};
  doc.querySelectorAll('[id^="f_"]').forEach((el) => {
    data[(el as HTMLElement).id.slice(2)] = el.textContent?.trim() || '';
  });
  return data;
};

const BASE_CSS = `
* { box-sizing: border-box; }
body { font-family: Arial, sans-serif; color: #1a1a1a; margin: 0; padding: 40px; font-size: 13px; line-height: 1.6; background: #fff; }
h2 { font-size: 14px; font-weight: 700; border-bottom: 2px solid #1a1a1a; padding-bottom: 5px; margin: 26px 0 10px; }
h3 { font-size: 13px; font-weight: 700; margin: 0 0 3px; }
p { margin: 7px 0; }
table { width: 100%; border-collapse: collapse; margin-top: 8px; }
th { background: #f3f4f6; text-align: left; padding: 8px 10px; font-size: 12px; border: 1px solid #e5e7eb; font-weight: 700; }
td { padding: 8px 10px; border: 1px solid #e5e7eb; font-size: 12px; vertical-align: middle; }
.lbl { font-size: 10px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 2px; }
.note { font-size: 11px; color: #9ca3af; margin-top: 20px; }
.sig-row { display: flex; gap: 40px; margin-top: 50px; }
.sig-block { flex: 1; }
.sig-line { border-top: 1.5px solid #1a1a1a; padding-top: 6px; font-size: 11px; color: #6b7280; margin-top: 38px; }
.hdr { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 26px; padding-bottom: 18px; border-bottom: 3px solid #1a1a1a; }
.big { font-size: 28px; font-weight: 900; letter-spacing: -1px; }
.two { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; margin: 14px 0; }
.box { background: #f9fafb; padding: 13px 15px; border-left: 3px solid #1a1a1a; }
.badge { display:inline-block; background:#1a1a1a; color:#fff; padding:3px 10px; border-radius:4px; font-size:11px; font-weight:700; }
`;

const EDIT_CSS = `
.e {
  outline: none;
  border-radius: 3px;
  cursor: text;
  min-width: 12px;
  display: inline-block;
  transition: background 0.15s, outline 0.15s;
  background: rgba(254, 243, 199, 0.55);
  border-bottom: 1.5px dashed #d97706;
  padding: 0 2px;
}
.e:hover { background: rgba(253, 230, 138, 0.75); outline: 1px solid #d97706; }
.e:focus { background: rgba(253, 230, 138, 0.9); outline: 2px solid #d97706; border-bottom: none; }
.e:empty:before { content: attr(data-ph); color: #b45309; pointer-events: none; font-style: italic; }
div.e { display: block; min-height: 20px; padding: 4px 6px; }
td.e { display: table-cell; }
`;

export const wrapDoc = (title: string, body: string, editable = false): string =>
  `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title>` +
  `<style>${BASE_CSS}${editable ? EDIT_CSS : ''}</style></head><body>${body}</body></html>`;

// ─── Template 2: Service Contract ─────────────────────────────────────────

const serviceBody = (d: Record<string, string>, ed: boolean) => `
<div class="hdr">
  <div>
    <div class="big">SERVICE CONTRACT</div>
    <div style="font-size:11px;color:#6b7280;margin-top:4px">
      Contract #: ${fe('contract_num', 'SC-2026-001', d.contract_num || '', ed)}
    </div>
  </div>
  <div style="text-align:right">
    <div class="lbl">Effective Date</div>
    <div style="font-weight:700">${fe('contract_date', today, d.contract_date || today, ed)}</div>
  </div>
</div>
<div class="two">
  <div class="box">
    <div class="lbl">Service Provider</div>
    <h3>${fe('provider_name', 'Your Company Name', d.provider_name || '', ed)}</h3>
    <div style="font-size:11px;color:#6b7280;margin-top:4px">
      ${fe('provider_address', '123 Main St, City, State ZIP', d.provider_address || '', ed)}<br>
      ${fe('provider_email', 'contact@yourcompany.com', d.provider_email || '', ed)}<br>
      ${fe('provider_phone', '(555) 000-0000', d.provider_phone || '', ed)}
    </div>
  </div>
  <div class="box">
    <div class="lbl">Client</div>
    <h3>${fe('client_name', 'Client Name', d.client_name || '', ed)}</h3>
    <div style="font-size:11px;color:#6b7280;margin-top:4px">
      ${fe('client_address', '456 Client Ave, City, State', d.client_address || '', ed)}<br>
      ${fe('client_email', 'client@example.com', d.client_email || '', ed)}<br>
      ${fe('client_phone', '(555) 111-2222', d.client_phone || '', ed)}
    </div>
  </div>
</div>
<h2>Scope of Services</h2>
<p>The Service Provider agrees to provide the following to the Client:</p>
${fbe('services_desc', 'Describe the services to be provided in detail...', d.services_desc || '', ed)}
<h2>Contract Term</h2>
<div class="two">
  <div><div class="lbl">Start Date</div><div style="font-weight:700">${fe('start_date', 'January 1, 2026', d.start_date || '', ed)}</div></div>
  <div><div class="lbl">End Date</div><div style="font-weight:700">${fe('end_date', 'December 31, 2026', d.end_date || '', ed)}</div></div>
</div>
<h2>Payment Terms</h2>
<table>
  <tr><th style="width:35%">Item</th><th>Details</th></tr>
  <tr><td>Rate / Fee</td><td>${fe('rate', '$0.00 per hour / fixed fee', d.rate || '', ed)}</td></tr>
  <tr><td>Payment Schedule</td><td>${fe('payment_schedule', '50% upfront, 50% on completion', d.payment_schedule || '', ed)}</td></tr>
  <tr><td>Late Fee</td><td>${fe('late_fee', '1.5% per month on overdue balances', d.late_fee || '1.5% per month', ed)}</td></tr>
</table>
<h2>Terms &amp; Conditions</h2>
<p><strong>Confidentiality:</strong> Both parties agree to keep all project information confidential.</p>
<p><strong>Intellectual Property:</strong> All work product shall be owned by ${fe('ip_owner', 'the Client upon full payment', d.ip_owner || 'the Client upon full payment', ed)}.</p>
<p><strong>Governing Law:</strong> This contract is governed by the laws of ${fe('governing_law', 'the State of [State]', d.governing_law || '', ed)}.</p>
<div class="sig-row">
  <div class="sig-block"><div class="sig-line">Service Provider Signature</div>
    <div style="margin-top:6px;font-size:12px">${fe('provider_sig_name', 'Printed Name &amp; Title', d.provider_sig_name || '', ed)}</div></div>
  <div class="sig-block"><div class="sig-line">Client Signature</div>
    <div style="margin-top:6px;font-size:12px">${fe('client_sig_name', 'Printed Name &amp; Title', d.client_sig_name || '', ed)}</div></div>
</div>
<p class="note">This agreement is the entire understanding between the parties regarding its subject matter.</p>`;

export const buildEditableServiceContract = (): string =>
  wrapDoc('Service Contract', serviceBody({}, true), true);
export const buildCleanServiceContract = (d: Record<string, string>): string =>
  wrapDoc('Service Contract', serviceBody(d, false));

// ─── Template 3: Modern Resume ─────────────────────────────────────────────

const resumeBody = (d: Record<string, string>, ed: boolean) => `
<style>
.resume-hdr { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:20px; padding-bottom:16px; border-bottom:3px solid #1a1a1a; }
.resume-name { font-size:30px; font-weight:900; letter-spacing:-1px; line-height:1; }
.resume-title { font-size:14px; color:#4b5563; font-weight:600; margin-top:4px; }
.resume-contact { font-size:11px; color:#6b7280; text-align:right; line-height:1.8; }
.exp-item { margin-bottom:16px; }
.exp-header { display:flex; justify-content:space-between; align-items:flex-start; }
.skill-list { display:flex; flex-wrap:wrap; gap:8px; margin-top:8px; }
.skill { background:#f3f4f6; border:1px solid #e5e7eb; padding:4px 10px; border-radius:20px; font-size:11px; font-weight:600; }
</style>
<div class="resume-hdr">
  <div>
    <div class="resume-name">${fe('name', 'Your Full Name', d.name || '', ed)}</div>
    <div class="resume-title">${fe('title', 'Your Professional Title', d.title || '', ed)}</div>
  </div>
  <div class="resume-contact">
    ${fe('email', 'email@example.com', d.email || '', ed)}<br>
    ${fe('phone', '(555) 000-0000', d.phone || '', ed)}<br>
    ${fe('location', 'City, State', d.location || '', ed)}<br>
    ${fe('linkedin', 'linkedin.com/in/yourname', d.linkedin || '', ed)}
  </div>
</div>
<h2>Professional Summary</h2>
${fbe('summary', 'Write a compelling 2-3 sentence summary of your professional background, key skills, and career goals...', d.summary || '', ed)}
<h2>Work Experience</h2>
<div class="exp-item">
  <div class="exp-header">
    <div><h3>${fe('job1_company', 'Company Name', d.job1_company || '', ed)}</h3>
      <div style="font-size:12px;color:#4b5563">${fe('job1_title', 'Job Title', d.job1_title || '', ed)}</div></div>
    <div style="font-size:11px;color:#6b7280;white-space:nowrap;margin-left:16px">${fe('job1_dates', 'Jan 2023 – Present', d.job1_dates || '', ed)}</div>
  </div>
  ${fbe('job1_bullets', '• Describe your key achievements and responsibilities here\n• Use action verbs and quantify results where possible', d.job1_bullets || '', ed)}
</div>
<div class="exp-item">
  <div class="exp-header">
    <div><h3>${fe('job2_company', 'Previous Company', d.job2_company || '', ed)}</h3>
      <div style="font-size:12px;color:#4b5563">${fe('job2_title', 'Previous Title', d.job2_title || '', ed)}</div></div>
    <div style="font-size:11px;color:#6b7280;white-space:nowrap;margin-left:16px">${fe('job2_dates', 'Jun 2020 – Dec 2022', d.job2_dates || '', ed)}</div>
  </div>
  ${fbe('job2_bullets', '• Describe your key achievements and responsibilities\n• Highlight measurable impact and outcomes', d.job2_bullets || '', ed)}
</div>
<div class="exp-item">
  <div class="exp-header">
    <div><h3>${fe('job3_company', 'Earlier Company', d.job3_company || '', ed)}</h3>
      <div style="font-size:12px;color:#4b5563">${fe('job3_title', 'Earlier Title', d.job3_title || '', ed)}</div></div>
    <div style="font-size:11px;color:#6b7280;white-space:nowrap;margin-left:16px">${fe('job3_dates', 'Jan 2018 – May 2020', d.job3_dates || '', ed)}</div>
  </div>
  ${fbe('job3_bullets', '• Key responsibilities and accomplishments', d.job3_bullets || '', ed)}
</div>
<h2>Education</h2>
<div style="display:flex;justify-content:space-between;align-items:flex-start">
  <div>
    <h3>${fe('edu_school', 'University Name', d.edu_school || '', ed)}</h3>
    <div style="font-size:12px;color:#4b5563">${fe('edu_degree', 'Bachelor of Science in [Field]', d.edu_degree || '', ed)}</div>
  </div>
  <div style="font-size:11px;color:#6b7280">${fe('edu_year', '2018', d.edu_year || '', ed)}</div>
</div>
<h2>Skills</h2>
<div class="skill-list">
  ${[1,2,3,4,5,6,7,8].map(i =>
    ed
      ? `<span class="skill e" contenteditable="true" id="f_skill${i}" data-ph="Skill ${i}">${d[`skill${i}`] || ''}</span>`
      : (d[`skill${i}`] ? `<span class="skill">${esc(d[`skill${i}`])}</span>` : '')
  ).join('')}
</div>`;

export const buildEditableResume = (): string =>
  wrapDoc('Modern Resume', resumeBody({}, true), true);
export const buildCleanResume = (d: Record<string, string>): string =>
  wrapDoc('Modern Resume', resumeBody(d, false));

// ─── Template 4: NDA Agreement ─────────────────────────────────────────────

const ndaBody = (d: Record<string, string>, ed: boolean) => `
<div class="hdr">
  <div>
    <div class="big">NON-DISCLOSURE</div>
    <div class="big" style="margin-top:-8px">AGREEMENT</div>
  </div>
  <div style="text-align:right">
    <div class="lbl">Effective Date</div>
    <div style="font-weight:700">${fe('effective_date', today, d.effective_date || today, ed)}</div>
  </div>
</div>
<p>This Non-Disclosure Agreement (the <strong>"Agreement"</strong>) is entered into as of the date above between:</p>
<div class="two">
  <div class="box">
    <div class="lbl">Disclosing Party</div>
    <h3>${fe('party1_name', 'Disclosing Party Name', d.party1_name || '', ed)}</h3>
    <div style="font-size:11px;color:#6b7280;margin-top:4px">
      ${fe('party1_address', 'Address, City, State ZIP', d.party1_address || '', ed)}<br>
      ${fe('party1_email', 'contact@party1.com', d.party1_email || '', ed)}
    </div>
  </div>
  <div class="box">
    <div class="lbl">Receiving Party</div>
    <h3>${fe('party2_name', 'Receiving Party Name', d.party2_name || '', ed)}</h3>
    <div style="font-size:11px;color:#6b7280;margin-top:4px">
      ${fe('party2_address', 'Address, City, State ZIP', d.party2_address || '', ed)}<br>
      ${fe('party2_email', 'contact@party2.com', d.party2_email || '', ed)}
    </div>
  </div>
</div>
<h2>1. Confidential Information</h2>
<p>"Confidential Information" means any non-public information disclosed by the Disclosing Party, including but not limited to:</p>
${fbe('conf_info_desc', 'Business plans, financial data, trade secrets, technical specifications, customer lists, proprietary processes, and any other information designated as confidential...', d.conf_info_desc || '', ed)}
<h2>2. Obligations</h2>
<p>The Receiving Party agrees to: (a) hold all Confidential Information in strict confidence; (b) not disclose it to any third parties without prior written consent; (c) use it solely for the purpose of ${fe('purpose', '[Stated Business Purpose]', d.purpose || '', ed)}.</p>
<h2>3. Exclusions</h2>
<p>This Agreement does not apply to information that is: (a) publicly available through no fault of the Receiving Party; (b) already known to the Receiving Party; (c) independently developed without reference to the Confidential Information.</p>
<h2>4. Term &amp; Termination</h2>
<p>This Agreement shall remain in effect for <strong>${fe('duration', '2 years', d.duration || '2 years', ed)}</strong> from the Effective Date, or until terminated by either party with 30 days written notice.</p>
<h2>5. Governing Law</h2>
<p>This Agreement shall be governed by the laws of ${fe('governing_law', 'the State of [State]', d.governing_law || '', ed)}, without regard to conflict of law principles.</p>
<div class="sig-row">
  <div class="sig-block"><div class="sig-line">Disclosing Party Signature</div>
    <div style="margin-top:6px;font-size:12px">${fe('party1_sig', 'Printed Name &amp; Title', d.party1_sig || '', ed)}</div></div>
  <div class="sig-block"><div class="sig-line">Receiving Party Signature</div>
    <div style="margin-top:6px;font-size:12px">${fe('party2_sig', 'Printed Name &amp; Title', d.party2_sig || '', ed)}</div></div>
</div>
<p class="note">This Agreement constitutes the entire agreement between the parties regarding confidentiality and supersedes all prior agreements.</p>`;

export const buildEditableNDA = (): string =>
  wrapDoc('Non-Disclosure Agreement', ndaBody({}, true), true);
export const buildCleanNDA = (d: Record<string, string>): string =>
  wrapDoc('Non-Disclosure Agreement', ndaBody(d, false));
